<template>
  <p class="lead">
    At Credsy, (hereinafter “Our”, or “We”), maintaining customer trust and confidence is one of our main priorities. We
    understand that you are concerned with how We treat any personal information We obtain from you, or from other
    sources about you, in the course of providing you with Our products and services. Accordingly, We want you to
    understand how We work to protect your privacy when We collect and use your information. We also want you to
    understand and be aware of the steps We take to safeguard your information.
  </p>
  <p class="lead">
    This Privacy Policy sets forth how Credsy uses and protects any information that We obtain from you when you use Our
    Site or Services. If Credsy chooses to modify its Privacy Policy, We will post those changes to Our Site and any
    other places We deem necessary and appropriate. Credsy reserves the right to modify its Privacy Policy at any time,
    so please review it frequently. Your access to and use of the Site is subject to the following Privacy Policy. By
    accessing the Site or creating an account, you accept Credsy’s Privacy Policy and you acknowledge that any other
    agreements between you and Credsy are superseded and of no force and effect, with respect to the subject matter
    addressed herein.
  </p>
  <hr />
  <h3 id="security-procedures">Security Procedures</h3>
  <p>
    At Credsy, We are dedicated to ensuring that any information you provide to Us is safe and secure. In order to
    protect the security of any information you provide to Us, We maintain physical, electronic, and procedural
    safeguards to protect the information We collect from you. We want you to feel confident using Our Site to conduct
    business. However, no system can be completely secure.
  </p>
  <h3 id="information-we-collect">Information We Collect</h3>
  <p>Credsy may collect the following types of information:</p>
  <ul>
    <li>
      Information from your account applications and other forms (for example, your name, address, and email address)
    </li>
    <li>Information about your transactions with us, Our affiliates, or other related parties</li>
    <li>Information about your internet connection, the equipment you use to access Our Services and usage details.</li>
  </ul>
  <h3 id="when-we-collect-information">When We Collect Information</h3>
  <p>Credsy may collect information from you including but not limited to the following times:</p>
  <ul>
    <li>Directly from you when you provide it to Us</li>
    <li>
      Automatically as you navigate through Our Site. This information may include your IP address, your approximate
      geographical location, your browser type and language, computer operating system, access time, the content of any
      undeleted cookies that your browser previously accepted from us and the referring Site address
    </li>
    <li>
      Only when We understand that you are not a citizen or resident of the European Union (in order to comply with
      GDPR)
    </li>
  </ul>
  <p>
    We may also collect information about you obtained in connection with Our efforts to protect against fraud or
    unauthorized use of your account.
  </p>
  <h3 id="what-we-do-with-your-information">What We Do With Your Information</h3>
  <p>
    We gather your information to understand your needs and provide you with better Service, and more specifically for
    the following reasons:
  </p>
  <ul>
    <li>Internal record keeping</li>
    <li>Maintaining your account</li>
    <li>To improve Our products and services</li>
    <li>
      To periodically send alerts, promotional email about new products, special offers or other information using the
      email address which you have provided
    </li>
    <li>To customize Our Site and your personal experience according to your interests</li>
  </ul>
  <p>Please note that your personal information will never be sold to any other third-party.</p>
  <h3 id="cookies-and-web-beacons">Cookies and Web Beacons</h3>
  <p>
    Our Site may use “cookies” and “web beacons” to speed navigation, keep track of items viewed, clicked on and/or
    shared, including links and advertisements, and provide you with custom-tailored services.
  </p>
  <p>
    A “web beacon” is an embedded GIF file that specifically helps us track whether you have viewed a specific piece of
    content. A cookie is a small file which asks permission to be placed on your computer’s hard drive. Once you agree,
    the file is added, and the cookie helps analyze web traffic or lets you know when you visit a particular site.
    Cookies allow web applications to respond to you as an individual. The web application can tailor its operations to
    your needs, likes and dislikes by gathering and remembering information about your preferences. We use traffic log
    cookies to identify which pages are being used. This helps us analyze data about web page traffic and improve Our
    Site in order to tailor it to customer needs. We only use this information for statistical analysis purposes and
    then the data is later removed from the system.
  </p>
  <p>
    Overall, cookies help us to provide you with a better-quality Site, by allowing us to monitor which pages you find
    useful and which you do not. Cookies in no way grant us access to your computer files or any information concerning
    you. You can choose to accept or decline cookies. However, this may prevent you from full access of Our Site and its
    features.
  </p>
  <h3 id="third-party-billing">Third-party Billing</h3>
  <p>
    We use a third-party payment processor to process payments made to us. In connection with the processing of such
    payments, We do not retain any financial information such as credit card numbers. Rather, all such information is
    provided directly to Our third-party processor, Stripe, whose use of your personal information is governed by their
    privacy policy.
  </p>
  <h3 id="parties-we-may-disclose-to">Parties We May Disclose To</h3>
  <p>
    We may disclose the information listed above to affiliates who assist us in providing certain products and services.
  </p>
  <p>
    We may also disclose your information to nonaffiliated third parties to process, service or administer transaction
    you authorized or initiated, maintain accounts, respond to court orders and legal investigations or as permitted by
    law.
  </p>
  <h3 id="amendments-to-our-privacy-policy">Amendments To Our Privacy Policy</h3>
  <p>
    Credsy retains the right to amend its Privacy Policy at any time, and from time to time, by posting the relevant
    amended Privacy Policy on Our Site. Your continued use of the Site or Services after the amended Privacy Policy
    effectively constitutes your agreement to, and acceptance of, the amended Privacy Policy.
  </p>
  <h3 id="contact-us">Contact us</h3>
  <p>
    If you have any questions about Our Privacy Policy, please contact Credsy by email at:
    <a href="mailto:info@credsy.com">info@credsy.com</a> or by phone at: <a href="tel:+18008492168">800-849-2168</a>.
  </p>
</template>

<script lang="ts" setup>
useCustomServerMeta({
  title: 'Privacy Policy',
  description:
    'This Privacy Policy is meant to help you understand what information we collect, why we collect it, and how you can update, manage, export, and delete your information.',
})

definePageMeta({
  layout: 'blog-owner',
  // Deterministic ids (used only as v-for :key). definePageMeta hoists this
  // menu to module scope, so a random nanoid() here would run at worker global
  // init — which Cloudflare workerd bans once the server is a single inlined
  // chunk. Static slugs are stable, unique, and also fix the SSR/client
  // hydration key mismatch that per-render random ids caused.
  menu: [
    {
      id: 'security-procedures',
      name: 'Security Procedures',
      href: '#security-procedures',
    },
    {
      id: 'information-we-collect',
      name: 'Information We Collect',
      href: '#information-we-collect',
    },
    {
      id: 'when-we-collect-information',
      name: 'When We Collect Information',
      href: '#when-we-collect-information',
    },
    {
      id: 'what-we-do-with-your-information',
      name: 'What We Do With Your Information',
      href: '#what-we-do-with-your-information',
    },
    {
      id: 'cookies-and-web-beacons',
      name: 'Cookies and Web Beacons',
      href: '#cookies-and-web-beacons',
    },
    {
      id: 'third-party-billing',
      name: 'Third-party Billing',
      href: '#third-party-billing',
    },
    {
      id: 'parties-we-may-disclose-to',
      name: 'Parties We May Disclose To',
      href: '#parties-we-may-disclose-to',
    },
    {
      id: 'amendments-to-our-privacy-policy',
      name: 'Amendments To Our Privacy Policy',
      href: '#amendments-to-our-privacy-policy',
    },
    {
      id: 'contact-us',
      name: 'Contact us',
      href: '#contact-us',
    },
  ],
})
</script>
